import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { PasswordInput } from '../components/ui/password-input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import {
  Key, Plus, Trash2, Copy, RefreshCw, Activity,
  Shield, Clock, CheckCircle, XCircle, Eye, EyeOff,
  Globe, Code, ChevronDown, ChevronUp, Database,
  FileText, Package, MapPin, Users, BarChart3, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const AVAILABLE_PERMISSIONS = [
  { key: 'read_inventory', label: 'Leer Inventario', desc: 'GET /api/external/inventory', icon: Package },
  { key: 'check_availability', label: 'Verificar Disponibilidad', desc: 'POST /api/external/inventory/check-availability', icon: Zap },
  { key: 'read_tracking', label: 'Rastreo de Equipo', desc: 'GET /api/external/tracking/*', icon: Activity },
  { key: 'read_maintenance', label: 'Mantenimiento', desc: 'GET /api/external/maintenance', icon: Database },
  { key: 'read_movements', label: 'Historial Movimientos', desc: 'GET /api/external/movements', icon: FileText },
  { key: 'read_categories', label: 'Leer Categorias', desc: 'GET /api/external/categories', icon: Database },
  { key: 'read_locations', label: 'Leer Ubicaciones', desc: 'GET /api/external/locations', icon: MapPin },
  { key: 'read_clients', label: 'Leer Clientes', desc: 'GET /api/external/clients', icon: Users },
];

const ApiPage = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [apiLogs, setApiLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyPerms, setNewKeyPerms] = useState(AVAILABLE_PERMISSIONS.map(p => p.key));
  const [createdKey, setCreatedKey] = useState(null);
  const [showDocs, setShowDocs] = useState(false);
  const [activeSection, setActiveSection] = useState('keys');

  // v5.4.19 — CRM bidirectional state
  const [crmCfg, setCrmCfg] = useState(null);
  const [crmWatches, setCrmWatches] = useState([]);
  const [crmLogs, setCrmLogs] = useState([]);
  const [crmSaving, setCrmSaving] = useState(false);
  const [crmTesting, setCrmTesting] = useState(false);
  const [crmRunning, setCrmRunning] = useState(false);

  const loadCrm = useCallback(async () => {
    try {
      const [cfgR, watchR, logR] = await Promise.all([
        api.get('/config/crm-callbacks').catch(() => null),
        api.get('/config/crm-callbacks/watches?limit=50').catch(() => null),
        api.get('/config/crm-callbacks/logs?limit=30').catch(() => null),
      ]);
      if (cfgR?.data) setCrmCfg(cfgR.data);
      if (watchR?.data) setCrmWatches(watchR.data.watches || []);
      if (logR?.data) setCrmLogs(logR.data.logs || []);
    } catch (e) {
      console.error('CRM load error', e);
    }
  }, []);

  useEffect(() => {
    if (activeSection === 'crm') loadCrm();
  }, [activeSection, loadCrm]);

  const saveCrm = async () => {
    setCrmSaving(true);
    try {
      await api.put('/config/crm-callbacks', crmCfg);
      toast.success('Configuración CRM guardada');
      loadCrm();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setCrmSaving(false);
    }
  };
  const testCrm = async (status) => {
    setCrmTesting(true);
    try {
      const r = await api.post('/config/crm-callbacks/test', { status });
      if (r.data.ok) {
        toast.success(`Callback de prueba enviado (HTTP ${r.data.http_status}) a ${r.data.url}`);
      } else if (r.data.skipped) {
        toast.warning(`Callback omitido: ${r.data.reason}`);
      } else {
        toast.error(`Callback falló: ${r.data.error || `HTTP ${r.data.http_status}`}`);
      }
      loadCrm();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error');
    } finally {
      setCrmTesting(false);
    }
  };
  const runCrmNow = async () => {
    setCrmRunning(true);
    try {
      await api.post('/config/crm-callbacks/run-now');
      toast.success('Reconciliación ejecutada');
      loadCrm();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error');
    } finally {
      setCrmRunning(false);
    }
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [keysRes, logsRes] = await Promise.all([
        api.get('/api-keys'),
        api.get('/api-logs?limit=50')
      ]);
      setApiKeys(keysRes.data || []);
      setApiLogs(logsRes.data || []);
    } catch (error) {
      console.error('Error loading API data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleCreateKey = async () => {
    if (!newKeyName.trim()) { toast.error('Nombre requerido'); return; }
    try {
      const res = await api.post('/api-keys', { name: newKeyName.trim(), permissions: newKeyPerms });
      setCreatedKey(res.data.key);
      setShowCreateModal(false);
      setNewKeyName('');
      setNewKeyPerms(AVAILABLE_PERMISSIONS.map(p => p.key));
      loadData();
      toast.success('API Key creada');
    } catch (error) {
      toast.error('Error al crear API Key');
    }
  };

  const handleToggleKey = async (keyId, isActive) => {
    try {
      await api.put(`/api-keys/${keyId}`, { is_active: !isActive });
      loadData();
      toast.success(isActive ? 'API Key desactivada' : 'API Key activada');
    } catch (error) {
      toast.error('Error');
    }
  };

  const handleDeleteKey = async (keyId) => {
    if (!window.confirm('Eliminar esta API Key permanentemente?')) return;
    try {
      await api.delete(`/api-keys/${keyId}`);
      loadData();
      toast.success('API Key eliminada');
    } catch (error) {
      toast.error('Error');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado al portapapeles');
  };

  const formatTimestamp = (ts) => {
    if (!ts) return 'Nunca';
    return new Date(ts).toLocaleString('es-MX', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  const baseUrl = window.location.origin;

  return (
    <div className="space-y-6" data-testid="api-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Globe className="h-6 w-6 text-primary" />
            API Externa
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Interconexion con sistemas externos (CRM, ERP). Gestiona API Keys y monitorea el uso.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowDocs(!showDocs)} data-testid="toggle-docs">
            <Code className="h-4 w-4 mr-2" />
            {showDocs ? 'Ocultar Docs' : 'Documentacion'}
          </Button>
          <Button variant="outline" size="sm" onClick={loadData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualizar
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="inline-flex h-10 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground gap-1 w-full max-w-2xl">
        {[
          { id: 'keys', label: 'API Keys', icon: Key },
          { id: 'logs', label: 'Logs', icon: Activity },
          { id: 'crm', label: 'CRM Bidireccional', icon: Zap },
          { id: 'docs', label: 'Documentacion', icon: FileText },
        ].map(tab => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveSection(tab.id)}
            className={`flex-1 inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-all cursor-pointer ${
              activeSection === tab.id ? 'bg-background text-foreground shadow-sm' : 'hover:bg-background/50'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon className="h-4 w-4 mr-2" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Created Key Alert */}
      {createdKey && (
        <Card className="border-green-500/50 bg-green-500/5">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="font-medium text-sm text-green-400">API Key creada exitosamente</p>
                  <p className="text-xs text-muted-foreground">Copia esta key ahora. No podras verla de nuevo.</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <code className="text-xs bg-muted px-3 py-1.5 rounded font-mono">{createdKey}</code>
                <Button size="sm" variant="outline" onClick={() => copyToClipboard(createdKey)}>
                  <Copy className="h-3 w-3" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setCreatedKey(null)}>
                  <XCircle className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* API Keys Section */}
      {activeSection === 'keys' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">API Keys ({apiKeys.length})</h2>
            <Button size="sm" onClick={() => setShowCreateModal(true)} data-testid="create-api-key-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nueva API Key
            </Button>
          </div>

          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : apiKeys.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <Key className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No hay API Keys creadas</p>
                <p className="text-sm mt-1">Crea una para conectar tu CRM u otro sistema externo</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {apiKeys.map(k => (
                <Card key={k.id} className={`${k.is_active ? '' : 'opacity-60'}`} data-testid={`api-key-${k.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${k.is_active ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                          <Key className={`h-5 w-5 ${k.is_active ? 'text-green-400' : 'text-red-400'}`} />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-sm">{k.name}</h3>
                            <Badge variant={k.is_active ? 'default' : 'destructive'} className="text-xs">
                              {k.is_active ? 'Activa' : 'Inactiva'}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground mt-1">
                            <span className="font-mono">{k.key_preview}</span>
                            <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{formatTimestamp(k.created_at)}</span>
                            <span className="flex items-center gap-1"><Activity className="h-3 w-3" />{k.call_count || 0} llamadas</span>
                            <span>Ultimo uso: {formatTimestamp(k.last_used)}</span>
                          </div>
                          <div className="flex gap-1 mt-1.5">
                            {(k.permissions || []).map(p => (
                              <Badge key={p} variant="outline" className="text-xs">{p}</Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleToggleKey(k.id, k.is_active)} title={k.is_active ? 'Desactivar' : 'Activar'}>
                          {k.is_active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteKey(k.id)} className="text-red-400 hover:text-red-300">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Logs Section */}
      {activeSection === 'logs' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-primary" />
              Registro de Llamadas API ({apiLogs.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {apiLogs.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No hay llamadas registradas</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="api-logs-table">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-3 text-sm font-medium">Fecha</th>
                      <th className="text-left py-2 px-3 text-sm font-medium">Key</th>
                      <th className="text-left py-2 px-3 text-sm font-medium">Metodo</th>
                      <th className="text-left py-2 px-3 text-sm font-medium">Endpoint</th>
                      <th className="text-left py-2 px-3 text-sm font-medium">IP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {apiLogs.map((log, i) => (
                      <tr key={i} className="border-b hover:bg-muted/30">
                        <td className="py-2 px-3 text-sm">{formatTimestamp(log.timestamp)}</td>
                        <td className="py-2 px-3 text-sm font-medium">{log.key_name}</td>
                        <td className="py-2 px-3">
                          <Badge variant="outline" className="text-xs font-mono">{log.method}</Badge>
                        </td>
                        <td className="py-2 px-3 text-sm font-mono text-muted-foreground">{log.path}</td>
                        <td className="py-2 px-3 text-sm text-muted-foreground">{log.ip}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* CRM Bidireccional Section (v5.4.19) */}
      {activeSection === 'crm' && (
        <div className="space-y-4" data-testid="crm-section">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-purple-500" />
                Configuración de Callbacks
              </CardTitle>
              <CardDescription>
                URLs donde el inventario notificará al CRM cuando se cree (o no) un movimiento con un control_number registrado por el CRM.
                El CRM debe llamar a <code>POST /api/external/crm/pending-events</code> con su X-API-Key para registrar el watch.
              </CardDescription>
              <div className="mt-3 p-3 rounded-lg border border-blue-500/30 bg-blue-500/5 text-xs space-y-1">
                <div className="font-semibold text-blue-700 dark:text-blue-300">⚠️ URL base del inventario para el CRM</div>
                <p className="text-muted-foreground">El CRM debe llamar al backend (NO al frontend cminventario.com, que solo sirve la SPA y devuelve HTML para rutas /api/*):</p>
                <code className="block bg-background p-2 rounded mt-1">https://api.cminventario.com</code>
                <p className="text-muted-foreground">Fallback durante transición DNS:</p>
                <code className="block bg-background p-2 rounded">https://entrada-y-salidas-cm-2026-production-c25e.up.railway.app</code>
                <p className="text-muted-foreground mt-2">Ping de verificación sin auth: <code>GET /api/external/health</code></p>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {!crmCfg ? (
                <div className="text-sm text-muted-foreground">Cargando...</div>
              ) : (
                <>
                  <div>
                    <Label className="text-xs">URL Producción</Label>
                    <Input
                      value={crmCfg.production_url || ''}
                      onChange={(e) => setCrmCfg({ ...crmCfg, production_url: e.target.value })}
                      placeholder="https://crm.cmidiomas.com/api/inventory-webhook/inbound"
                      data-testid="crm-prod-url"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">URL Preview (pruebas)</Label>
                    <Input
                      value={crmCfg.preview_url || ''}
                      onChange={(e) => setCrmCfg({ ...crmCfg, preview_url: e.target.value })}
                      placeholder="https://recipient-config.preview.emergentagent.com/api/inventory-webhook/inbound"
                      data-testid="crm-preview-url"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Entorno activo</Label>
                    <div className="flex gap-2 mt-1 flex-wrap">
                      {['production', 'preview', 'both', 'off'].map((env) => (
                        <button
                          key={env}
                          type="button"
                          onClick={() => setCrmCfg({ ...crmCfg, active_env: env })}
                          className={`px-3 py-1.5 rounded-md text-sm border ${
                            crmCfg.active_env === env
                              ? 'bg-primary text-primary-foreground border-primary'
                              : 'bg-background hover:bg-muted'
                          }`}
                          data-testid={`crm-env-${env}`}
                        >
                          {env === 'both' ? 'ambos (prod + preview)' : env}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      <b>ambos</b>: cada callback se envía a las DOS URLs simultáneamente. Útil para pruebas en preview sin dejar de notificar a producción.
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs">
                      Secret compartido (opcional · se envía en header <code>X-Inventario-Secret</code>)
                    </Label>
                    <PasswordInput
                      placeholder={crmCfg.shared_secret_set ? '••••••••• (dejar vacío para no cambiar)' : 'Sin secret'}
                      onChange={(e) => setCrmCfg({ ...crmCfg, shared_secret: e.target.value })}
                      data-testid="crm-secret"
                    />
                  </div>
                  <div className="flex gap-2 pt-2 border-t flex-wrap">
                    <Button onClick={saveCrm} disabled={crmSaving} data-testid="crm-save">
                      {crmSaving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                      Guardar
                    </Button>
                    <Button variant="outline" onClick={() => testCrm('pending')} disabled={crmTesting} data-testid="crm-test-pending">
                      Probar callback &quot;pending&quot;
                    </Button>
                    <Button variant="outline" onClick={() => testCrm('done')} disabled={crmTesting} data-testid="crm-test-done">
                      Probar callback &quot;done&quot;
                    </Button>
                    <Button variant="outline" onClick={runCrmNow} disabled={crmRunning} data-testid="crm-run-now">
                      {crmRunning ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Zap className="h-4 w-4 mr-2" />}
                      Ejecutar reconciliación ahora
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Active watches */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Watches activos ({crmWatches.length})
              </CardTitle>
              <CardDescription>
                control_numbers registrados por el CRM que el inventario está vigilando.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {crmWatches.length === 0 ? (
                <div className="text-sm text-muted-foreground p-3 border rounded-lg">
                  Sin watches registrados. El CRM puede crear uno con <code>POST /api/external/crm/pending-events</code>.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="crm-watches-table">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-3">Control #</th>
                        <th className="text-left py-2 px-3">Estado</th>
                        <th className="text-left py-2 px-3">Módulo</th>
                        <th className="text-left py-2 px-3">Evento</th>
                        <th className="text-left py-2 px-3">Registrado</th>
                        <th className="text-left py-2 px-3">Callbacks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {crmWatches.map((w) => (
                        <tr key={w.control_number} className="border-b hover:bg-muted/30">
                          <td className="py-2 px-3 font-mono">{w.control_number}</td>
                          <td className="py-2 px-3">
                            <Badge variant={w.status === 'done' ? 'default' : w.status === 'expired' ? 'destructive' : 'secondary'}>
                              {w.status}
                            </Badge>
                          </td>
                          <td className="py-2 px-3 text-muted-foreground">{w.module_type || '—'}</td>
                          <td className="py-2 px-3 text-muted-foreground">{w.event_name || '—'}</td>
                          <td className="py-2 px-3 text-muted-foreground">{(w.registered_at || '').slice(0, 16).replace('T', ' ')}</td>
                          <td className="py-2 px-3 text-muted-foreground">{w.callback_fired_count || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent callback logs */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Activity className="h-4 w-4" />
                Últimos callbacks enviados ({crmLogs.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {crmLogs.length === 0 ? (
                <div className="text-sm text-muted-foreground p-3 border rounded-lg">Sin callbacks aún.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="crm-logs-table">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-3">Fecha</th>
                        <th className="text-left py-2 px-3">Control #</th>
                        <th className="text-left py-2 px-3">Status</th>
                        <th className="text-left py-2 px-3">Env</th>
                        <th className="text-left py-2 px-3">HTTP</th>
                        <th className="text-left py-2 px-3">OK</th>
                      </tr>
                    </thead>
                    <tbody>
                      {crmLogs.map((l, i) => (
                        <tr key={i} className="border-b hover:bg-muted/30">
                          <td className="py-2 px-3 text-muted-foreground">{(l.timestamp || '').slice(0, 19).replace('T', ' ')}</td>
                          <td className="py-2 px-3 font-mono">{l.control_number}</td>
                          <td className="py-2 px-3">
                            <Badge variant="outline">{l.status}</Badge>
                          </td>
                          <td className="py-2 px-3 text-muted-foreground">{l.env}</td>
                          <td className="py-2 px-3 text-muted-foreground">{l.http_status || '—'}</td>
                          <td className="py-2 px-3">
                            {l.ok ? <CheckCircle className="h-4 w-4 text-green-500" /> : <XCircle className="h-4 w-4 text-red-500" />}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Documentation Section */}
      {activeSection === 'docs' && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                Documentacion de la API
              </CardTitle>
              <CardDescription>
                Todos los endpoints requieren header <code className="bg-muted px-1.5 py-0.5 rounded text-xs font-mono">X-API-Key: tu_api_key</code>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-3 rounded-lg bg-muted/30 border">
                <p className="text-sm font-medium mb-1">Base URL</p>
                <div className="flex items-center gap-2">
                  <code className="text-xs bg-muted px-2 py-1 rounded font-mono flex-1">{baseUrl}/api/external</code>
                  <Button size="sm" variant="ghost" onClick={() => copyToClipboard(`${baseUrl}/api/external`)}>
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
              </div>

              {[
                {
                  method: 'GET', path: '/inventory', title: 'Inventario Completo',
                  desc: 'Retorna todo el inventario con stock, disponibilidad, ubicaciones y seriales.',
                  response: '{ items: [{ id, name, category_name, total_stock, total_available, total_assigned, locations: [{ location_name, box_label, available, serials_available }] }] }'
                },
                {
                  method: 'GET', path: '/inventory/summary', title: 'Resumen por Categoria',
                  desc: 'Inventario agrupado por categoria con totales.',
                  response: '{ categories: [{ category, equipment_types, total_stock, total_available }], grand_total: {...} }'
                },
                {
                  method: 'POST', path: '/inventory/check-availability', title: 'Verificar Disponibilidad (Evento)',
                  desc: 'Verifica si hay suficiente inventario para cubrir los requerimientos de un evento. Retorna detalle por item con ubicaciones que tienen stock.',
                  body: '{ event_name: "Evento X", event_date: "2026-04-15", requirements: [{ equipment_name: "Receptor", quantity_needed: 50 }, { category_name: "Audio", quantity_needed: 10 }] }',
                  response: '{ all_requirements_covered: true/false, details: [{ status: "covered"|"insufficient"|"not_found", available, deficit, matching_equipment: [{ locations_with_stock }] }] }'
                },
                {
                  method: 'GET', path: '/categories', title: 'Categorias',
                  desc: 'Lista todas las categorias de equipo.',
                  response: '{ items: [{ id, name }] }'
                },
                {
                  method: 'GET', path: '/locations', title: 'Ubicaciones',
                  desc: 'Lista todas las ubicaciones/almacenes.',
                  response: '{ items: [{ id, name }] }'
                },
                {
                  method: 'GET', path: '/clients', title: 'Clientes',
                  desc: 'Lista todos los clientes registrados.',
                  response: '{ items: [{ id, name, city, state, email, phone, contacts }] }'
                },
                {
                  method: 'GET', path: '/tracking/active', title: 'Equipo en Campo (Activos)',
                  desc: 'Salidas sin retorno: que equipo esta fuera, en que evento, quien lo llevo, cuantos dias fuera.',
                  response: '{ total_active_checkouts, total_items_out, assignments: [{ event_name, client_name, responsible_name, days_out, items: [{ equipment_name, quantity }] }] }'
                },
                {
                  method: 'GET', path: '/tracking/equipment/{id}', title: 'Status de Equipo Especifico',
                  desc: 'Estado detallado: ubicaciones con stock, movimientos recientes, historial de mantenimiento.',
                  response: '{ equipment: { name, total_stock, total_available }, locations: [...], recent_movements: [...], maintenance: [...] }'
                },
                {
                  method: 'GET', path: '/maintenance', title: 'Mantenimiento',
                  desc: 'Registros de mantenimiento. Filtro opcional: ?status=pendiente|en_progreso|completado',
                  response: '{ items: [{ equipment_name, type, status, priority, description, scheduled_date }] }'
                },
                {
                  method: 'GET', path: '/movements', title: 'Historial de Movimientos',
                  desc: 'Historial completo. Filtros: ?limit=100&type=salida',
                  response: '{ items: [{ type, event_name, client_name, responsible_name, date, items: [...] }] }'
                },
              ].map((ep) => (
                <div key={ep.path} className="border rounded-lg overflow-hidden">
                  <div className="p-3 bg-muted/30 flex items-center gap-3">
                    <Badge className={`font-mono text-xs ${ep.method === 'POST' ? 'bg-amber-500/20 text-amber-400' : 'bg-green-500/20 text-green-400'}`}>
                      {ep.method}
                    </Badge>
                    <code className="text-sm font-mono font-medium">/api/external{ep.path}</code>
                    <span className="text-sm text-muted-foreground ml-auto">{ep.title}</span>
                  </div>
                  <div className="p-3 space-y-2">
                    <p className="text-sm text-muted-foreground">{ep.desc}</p>
                    {ep.body && (
                      <div>
                        <p className="text-xs font-medium mb-1">Body (JSON):</p>
                        <pre className="text-xs bg-muted p-2 rounded font-mono overflow-x-auto whitespace-pre-wrap">{ep.body}</pre>
                      </div>
                    )}
                    <div>
                      <p className="text-xs font-medium mb-1">Respuesta:</p>
                      <pre className="text-xs bg-muted p-2 rounded font-mono overflow-x-auto whitespace-pre-wrap">{ep.response}</pre>
                    </div>
                  </div>
                </div>
              ))}

              <div className="p-3 rounded-lg bg-primary/10 border border-primary/30">
                <p className="text-sm font-medium flex items-center gap-2 mb-2">
                  <Zap className="h-4 w-4 text-primary" />
                  Ejemplo: Verificar disponibilidad para evento desde CRM
                </p>
                <pre className="text-xs bg-muted p-3 rounded font-mono overflow-x-auto whitespace-pre-wrap">{`curl -X POST "${baseUrl}/api/external/inventory/check-availability" \\
  -H "X-API-Key: cm_tu_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "event_name": "Congreso ABC",
    "event_date": "2026-05-20",
    "requirements": [
      {"equipment_name": "Receptor", "quantity_needed": 100},
      {"equipment_name": "Transmisor", "quantity_needed": 5},
      {"category_name": "Audio", "quantity_needed": 20}
    ]
  }'`}</pre>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Create API Key Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5 text-primary" />
              Nueva API Key
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Nombre / Sistema</Label>
              <Input
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                placeholder="Ej: CRM CM Idiomas"
                data-testid="api-key-name-input"
              />
            </div>
            <div>
              <Label className="mb-2 block">Permisos</Label>
              <div className="space-y-2">
                {AVAILABLE_PERMISSIONS.map(p => (
                  <label key={p.key} className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-colors ${
                    newKeyPerms.includes(p.key) ? 'bg-primary/10 border border-primary/30' : 'bg-muted/20 hover:bg-muted/40 border border-transparent'
                  }`}>
                    <Checkbox
                      checked={newKeyPerms.includes(p.key)}
                      onCheckedChange={(checked) => {
                        setNewKeyPerms(prev => checked ? [...prev, p.key] : prev.filter(k => k !== p.key));
                      }}
                    />
                    <div>
                      <p className="text-sm font-medium">{p.label}</p>
                      <p className="text-xs text-muted-foreground font-mono">{p.desc}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancelar</Button>
            <Button onClick={handleCreateKey} data-testid="confirm-create-key">
              <Key className="h-4 w-4 mr-2" />
              Generar API Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApiPage;
